/**
 * Page View Tracking Hook
 *
 * This hook handles analytics page view tracking based on route changes.
 * Separated from navigation concerns to maintain clean separation of concerns.
 * Exported as a custom hook to be used at the application root level (e.g., in the main layout or router).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../services/firebaseAnalytics';

/**
 * Extracts a granular screen name from a pathname
 * Examples: / -> home, /chat -> chat, /documents/123 -> documents_view, /documents/123/edit -> documents_edit
 */
const getScreenName = (pathname: string): string => {
  if (pathname === '/') return 'home';

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'unknown';

  const mainSection = segments[0]; // e.g., 'documents', 'chat', 'profile'

  // For paths with identifiers and actions (e.g., /documents/123/edit)
  if (segments.length >= 3) {
    const action = segments[2]; // e.g., 'edit', 'share', 'export'
    return `${mainSection}_${action}`;
  }

  // For paths with identifiers only (e.g., /documents/123)
  // Check if second segment is an ID (numeric or UUID-like) vs a named route
  if (segments.length === 2) {
    const secondSegment = segments[1];
    // Simple heuristic: if it looks like an ID (numeric, UUID pattern, or hash), it's a view action
    const isLikelyId =
      /^(\d+|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[\w-]+)$/.test(
        secondSegment,
      );
    if (isLikelyId) {
      return `${mainSection}_view`;
    }
    // Otherwise treat second segment as a named sub-route (e.g., /settings/profile -> settings_profile)
    return `${mainSection}_${secondSegment}`;
  }

  // Single-segment paths (e.g., /chat, /profile)
  return mainSection;
};

/**
 * Custom hook to track page views in Firebase Analytics
 *
 * This hook should be used at the application root or layout level to track
 * page views as the user navigates through the application.
 *
 * Usage example:
 * ```tsx
 * function AppRoot() {
 *   usePageViewTracking();
 *   return <YourAppContent />;
 * }
 * ```
 *
 * Routes excluded from tracking:
 * - /login
 * - /signup
 * - /forgot-password
 * - /auth/* (OAuth callback routes)
 */
export const usePageViewTracking = (): void => {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Skip tracking for auth routes
    if (
      location.pathname === '/login' ||
      location.pathname === '/signup' ||
      location.pathname === '/forgot-password' ||
      location.pathname.startsWith('/auth/')
    ) {
      return;
    }

    // Prevent double-tracking in React Strict Mode or redundant renders
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;

    // Extract granular screen name from path
    const screenName = getScreenName(location.pathname);

    trackPageView(screenName, location.pathname).catch((error) => {
      // Silently fail - analytics shouldn't break the app
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to track page view:', error);
      }
    });
  }, [location.pathname]);
};

export default usePageViewTracking;
